import React, { useState, useEffect } from "react";
import {
	Button,
	Table,
	Modal,
	Input,
	Upload,
	message,
	Breadcrumb,
	Space,
	Dropdown,
	Select,
	Tag,
	Typography,
	List,
	Avatar,
	Divider,
} from "antd";
import {
	FolderOutlined,
	FileOutlined,
	PlusOutlined,
	UploadOutlined,
	DeleteOutlined,
	EditOutlined,
	MoreOutlined,
	HomeOutlined,
	FolderOpenOutlined,
	ShareAltOutlined,
	UserOutlined,
	LockOutlined,
	UnlockOutlined,
	TeamOutlined,
	DownloadOutlined,
	FileTextOutlined,
	FileImageOutlined,
	FileWordOutlined,
	FilePptOutlined,
	FileExcelOutlined,
	FilePdfOutlined,
	FileZipOutlined,
} from "@ant-design/icons";
import axios from "axios";
import Layout from "../layout/Layout";
import { api } from "../utils/Api";

const { Title, Text } = Typography;
const { Option } = Select;

const Manager57FilesRoot = () => {
	const [currentFolder, setCurrentFolder] = useState(null);
	const [folders, setFolders] = useState([]);
	const [files, setFiles] = useState([]);
	const [breadcrumb, setBreadcrumb] = useState([]);
	const [loading, setLoading] = useState(false);
	const [users, setUsers] = useState([]);

	// Modals
	const [createFolderModal, setCreateFolderModal] = useState(false);
	const [uploadFileModal, setUploadFileModal] = useState(false);
	const [renameModal, setRenameModal] = useState(false);
	const [shareModal, setShareModal] = useState(false);
	const [viewSharedModal, setViewSharedModal] = useState(false);

	// Form states
	const [newFolderName, setNewFolderName] = useState("");
	const [fileList, setFileList] = useState([]);
	const [renameItem, setRenameItem] = useState(null);
	const [newName, setNewName] = useState("");
	const [shareItem, setShareItem] = useState(null);
	const [selectedUsers, setSelectedUsers] = useState([]);
	const [sharePermission, setSharePermission] = useState("view");
	const [sharedUsers, setSharedUsers] = useState([]);
	const [folderOwner, setFolderOwner] = useState(null);

	useEffect(() => {
		loadRootFolders();
		loadUsers();
	}, []);

	// Load all users for sharing
	const loadUsers = async () => {
		try {
			const token = localStorage.getItem("token");
			const { data } = await axios.get(`${api}/user/files/list`, {
				headers: { Authorization: `Bearer ${token}` },
			});

			if (data.success) {
				setUsers(data.payload);
			} else {
				message.error(data.message);
			}
		} catch (error) {
			console.error("Error loading users:", error);
		}
	};

	// Load root folders
	const loadRootFolders = async () => {
		setLoading(true);
		try {
			const token = localStorage.getItem("token");
			const { data } = await axios.get(`${api}/folder/get`, {
				headers: { Authorization: `Bearer ${token}` },
			});

			setFolders(data.payload);
			setFiles([]);
			setCurrentFolder(null);
			setBreadcrumb([]);
		} catch (error) {
			message.error("Error loading folders");
			console.error(error);
		}
		setLoading(false);
	};

	// Load folder contents
	const loadFolder = async (folderId) => {
		setLoading(true);
		try {
			const token = localStorage.getItem("token");

			const { data: foldersData } = await axios.get(
				`${api}/folder/get/${folderId}`,
				{
					headers: { Authorization: `Bearer ${token}` },
				}
			);

			const { data: breadcrumbData } = await axios.get(
				`${api}/folder/get/${folderId}/breadcrumb`,
				{
					headers: { Authorization: `Bearer ${token}` },
				}
			);

			if (!foldersData.success) {
				message.error(foldersData.message);
			} else {
				setFolders(foldersData.payload.subfolders);
				setFiles(foldersData.payload.files);
				setCurrentFolder(foldersData.payload.folder);
			}

			if (!breadcrumbData.success) {
				message.error(breadcrumbData.message);
			} else {
				setBreadcrumb(breadcrumbData.payload);
			}
		} catch (error) {
			message.error(error.response?.data?.error || "Error loading folder");
			console.error(error);
		}
		setLoading(false);
	};

	// Create new folder
	const handleCreateFolder = async () => {
		if (!newFolderName.trim()) {
			message.warning("Please enter a folder name");
			return;
		}

		try {
			const token = localStorage.getItem("token");
			const { data } = await axios.post(
				`${api}/folder/create`,
				{
					name: newFolderName,
					parentFolderId: currentFolder?._id || null,
				},
				{
					headers: { Authorization: `Bearer ${token}` },
				}
			);

			if (data.success) {
				message.success(data.message);
			} else {
				message.success(data.message);
			}

			setCreateFolderModal(false);
			setNewFolderName("");

			if (currentFolder) {
				loadFolder(currentFolder._id);
			} else {
				loadRootFolders();
			}
		} catch (error) {
			message.error(error.response?.data?.error || "Error creating folder");
			console.error(error);
		}
	};

	// Upload file
	const handleUploadFile = async () => {
		if (!currentFolder) {
			message.warning("Please select a folder first");
			return;
		}

		if (fileList.length === 0) {
			message.warning("Please select a file");
			return;
		}

		const formData = new FormData();
		formData.append("file", fileList[0]);
		formData.append("folderId", currentFolder._id);

		try {
			const token = localStorage.getItem("token");
			const { data } = await axios.post(`${api}/file/create`, formData, {
				headers: {
					Authorization: `Bearer ${token}`,
					"Content-Type": "multipart/form-data",
				},
			});

			if (data.success) {
				message.success(data.message);
			} else {
				message.error(data.message);
			}

			setUploadFileModal(false);
			setFileList([]);
			loadFolder(currentFolder._id);
		} catch (error) {
			message.error(error.response?.data?.error || "Error uploading file");
			console.error(error);
		}
	};

	// Share folder or file
	const handleShare = async () => {
		if (selectedUsers.length === 0) {
			message.warning("Please select at least one user");
			return;
		}

		try {
			const token = localStorage.getItem("token");
			const endpoint =
				shareItem.type === "folder"
					? `${api}/folder/${shareItem.id}/share`
					: `${api}/file/${shareItem.id}/share`;

			await axios.post(
				endpoint,
				{
					userIds: selectedUsers,
					permission: sharePermission,
				},
				{
					headers: { Authorization: `Bearer ${token}` },
				}
			);

			message.success(
				`${shareItem.type === "folder" ? "Folder" : "File"} shared successfully`
			);
			setShareModal(false);
			setShareItem(null);
			setSelectedUsers([]);
			setSharePermission("view");

			if (currentFolder) {
				loadFolder(currentFolder._id);
			} else {
				loadRootFolders();
			}
		} catch (error) {
			message.error(error.response?.data?.error || "Error sharing");
			console.error(error);
		}
	};

	// View shared users
	const handleViewSharedUsers = async (item, type) => {
		try {
			const token = localStorage.getItem("token");
			const endpoint =
				type === "folder"
					? `${api}/folder/${item._id}/shared-users`
					: `${api}/file/${item._id}/shared-users`;

			const { data } = await axios.get(endpoint, {
				headers: { Authorization: `Bearer ${token}` },
			});

			setSharedUsers(data.payload.sharedWith);
			setFolderOwner(data.payload.owner);
			setShareItem({
				id: item._id,
				type,
				name: type === "folder" ? item.name : item.originalName,
			});
			setViewSharedModal(true);
		} catch (error) {
			message.error("Error loading shared users");
			console.error(error);
		}
	};

	// Remove user access
	const handleRemoveAccess = async (userId) => {
		try {
			const token = localStorage.getItem("token");
			const endpoint =
				shareItem.type === "folder"
					? `${api}/folder/${shareItem.id}/share/${userId}`
					: `${api}/file/${shareItem.id}/share/${userId}`;

			await axios.delete(endpoint, {
				headers: { Authorization: `Bearer ${token}` },
			});

			message.success("Access removed successfully");
			handleViewSharedUsers({ _id: shareItem.id }, shareItem.type);
		} catch (error) {
			message.error("Error removing access");
			console.error(error);
		}
	};

	// Delete folder
	const handleDeleteFolder = async (folderId) => {
		try {
			const token = localStorage.getItem("token");
			const { data } = await axios.delete(`${api}/folder/delete/${folderId}`, {
				headers: { Authorization: `Bearer ${token}` },
			});

			if (data.success) {
				message.success(data.message);
				if (currentFolder) {
					loadFolder(currentFolder._id);
				} else {
					loadRootFolders();
				}
			} else {
				message.error(data.message);
			}
		} catch (error) {
			message.error(error.response?.data?.error || "Error deleting folder");
			console.error(error);
		}
	};

	// Delete file
	const handleDeleteFile = async (fileId) => {
		try {
			const token = localStorage.getItem("token");
			const { data } = await axios.delete(`${api}/file/delete/${fileId}`, {
				headers: { Authorization: `Bearer ${token}` },
			});

			if (data.success) {
				message.success(data.message);
				loadFolder(currentFolder._id);
			} else {
				message.error(data.message);
			}
		} catch (error) {
			message.error(error.response?.data?.error || "Error deleting file");
			console.error(error);
		}
	};

	// Rename folder/file
	const handleRename = async () => {
		if (!newName.trim()) {
			message.warning("Please enter a new name");
			return;
		}

		try {
			const token = localStorage.getItem("token");

			if (renameItem.type === "folder") {
				const { data } = await axios.put(
					`${api}/folder/update/${renameItem.id}`,
					{ name: newName },
					{ headers: { Authorization: `Bearer ${token}` } }
				);
				if (data.success) {
					message.success(data.message);
				} else {
					message.error(data.message);
				}
			} else {
				const { data } = await axios.put(
					`${api}/file/update/${renameItem.id}`,
					{ originalName: newName },
					{ headers: { Authorization: `Bearer ${token}` } }
				);
				if (data.success) {
					message.success(data.message);
				} else {
					message.error(data.message);
				}
			}

			setRenameModal(false);
			setRenameItem(null);
			setNewName("");

			if (currentFolder) {
				loadFolder(currentFolder._id);
			} else {
				loadRootFolders();
			}
		} catch (error) {
			message.error(error.response?.data?.error || "Error renaming");
			console.error(error);
		}
	};

	// Open modals
	const openRenameModal = (item, type) => {
		setRenameItem({ id: item._id, type });
		setNewName(type === "folder" ? item.name : item.originalName);
		setRenameModal(true);
	};

	const openShareModal = (item, type) => {
		setShareItem({
			id: item._id,
			type,
			name: type === "folder" ? item.name : item.originalName,
		});
		setShareModal(true);
	};

	// Get permission tag color
	const getPermissionColor = (permission) => {
		switch (permission) {
			case "admin":
				return "red";
			case "edit":
				return "orange";
			case "view":
				return "green";
			default:
				return "default";
		}
	};

	const getFileIcon = (extension) => {
		switch (extension) {
			case ".pdf":
				return { icon: <FilePdfOutlined />, color: "#ff4d4f" };

			case ".xlsx":
			case ".xls":
				return { icon: <FileExcelOutlined />, color: "#52c41a" };

			case ".doc":
			case ".docx":
				return { icon: <FileWordOutlined />, color: "#2f54eb" };

			case ".ppt":
			case ".pptx":
				return { icon: <FilePptOutlined />, color: "#fa8c16" };

			case ".jpg":
			case ".jpeg":
			case ".png":
			case ".PNG":
			case ".JPE":
			case ".JPEG":
				return { icon: <FileImageOutlined />, color: "#13c2c2" };

			case ".txt":
				return { icon: <FileTextOutlined />, color: "#595959" };
			case ".zip":
				return { icon: <FileZipOutlined />, color: "#595959" };

			default:
				return { icon: <FileOutlined />, color: "#8c8c8c" };
		}
	};

	// Table columns
	const columns = [
		{
			title: "Name",
			dataIndex: "name",
			key: "name",
			render: (text, record) => {
				const isPathOnly = record.isPathOnly || false;
				const hasInheritedAccess = record.hasInheritedAccess || false;

				const fileInfo = getFileIcon(record.extension);

				return (
					<Space>
						{record.type === "folder" ? (
							<>
								<FolderOutlined
									style={{
										fontSize: 20,
										color: isPathOnly ? "#d9d9d9" : "#1890ff",
									}}
								/>
								<Button
									type="link"
									onClick={() => loadFolder(record._id)}
									style={{
										padding: 0,
										color: isPathOnly ? "#8c8c8c" : undefined,
									}}
								>
									{text}
								</Button>
								{!record.isOwner && hasInheritedAccess && (
									<Tag color="cyan" icon={<ShareAltOutlined />}>
										Inherited
									</Tag>
								)}
								{!record.isOwner && !hasInheritedAccess && !isPathOnly && (
									<Tag color="blue" icon={<ShareAltOutlined />}>
										Shared
									</Tag>
								)}
								{isPathOnly && <Tag color="default">Path Only</Tag>}
							</>
						) : (
							<>
								{/* <FileOutlined style={{ fontSize: 20, color: "#52c41a" }} /> */}
								{React.cloneElement(fileInfo.icon, {
									style: { fontSize: 20, color: fileInfo.color },
								})}
								<span>{record.originalName || text}</span>
								{!record.isOwner && hasInheritedAccess && (
									<Tag color="cyan" icon={<ShareAltOutlined />}>
										Inherited
									</Tag>
								)}
								{!record.isOwner && !hasInheritedAccess && (
									<Tag color="blue" icon={<ShareAltOutlined />}>
										Shared
									</Tag>
								)}
							</>
						)}
					</Space>
				);
			},
		},
		{
			title: "Permission",
			dataIndex: "userPermission",
			key: "permission",
			width: 120,
			render: (permission, record) => {
				if (record.isPathOnly) {
					return <Tag color="default">Navigation</Tag>;
				}
				if (record.isOwner) {
					return (
						<Tag color="gold" icon={<LockOutlined />}>
							Owner
						</Tag>
					);
				}
				// Show inherited permission
				return (
					<Tag color={getPermissionColor(permission)}>
						{permission?.toUpperCase()}
					</Tag>
				);
			},
		},
		{
			title: "Size",
			dataIndex: "fileSize",
			key: "size",
			width: 120,
			render: (size) => (size ? formatFileSize(size) : "-"),
		},
		{
			title: "Modified",
			dataIndex: "updatedAt",
			key: "modified",
			width: 180,
			render: (date) => new Date(date).toLocaleString(),
		},
		{
			title: "Owner",
			dataIndex: "owner",
			key: "owner",
			width: 180,
			render: (value) => <Text>{value?.name || ""}</Text>,
		},
		{
			title: "Actions",
			key: "actions",
			width: 100,
			render: (_, record) => {
				// Path-only folders should not show any actions
				if (record.isPathOnly) {
					return null;
				}

				const canEdit =
					record.isOwner || ["edit", "admin"].includes(record.userPermission);
				const canShare = record.isOwner || record.userPermission === "admin";
				const canDelete = record.isOwner || record.userPermission === "admin";

				const items = [
					record.type === "file" && {
						key: "file-download",
						label: "Download",
						icon: <DownloadOutlined />,
						onClick: () => window.open(record.fileUrl, "_blank"),
					},
					canShare &&
						record.type === "folder" && {
							key: "share",
							label: "Share",
							icon: <ShareAltOutlined />,
							onClick: () => openShareModal(record, record.type),
						},
					canShare &&
						record.type === "folder" && {
							key: "view-shared",
							label: "View Access",
							icon: <TeamOutlined />,
							onClick: () => handleViewSharedUsers(record, record.type),
						},
					canEdit && {
						key: "rename",
						label: "Rename",
						icon: <EditOutlined />,
						onClick: () => openRenameModal(record, record.type),
					},
					canDelete && {
						key: "delete",
						label: "Delete",
						icon: <DeleteOutlined />,
						danger: true,
						onClick: () => {
							Modal.confirm({
								title: `Delete ${record.type}?`,
								content: "This action cannot be undone",
								onOk: () => {
									if (record.type === "folder") {
										handleDeleteFolder(record._id);
									} else {
										handleDeleteFile(record._id);
									}
								},
							});
						},
					},
				].filter(Boolean);

				return items.length > 0 ? (
					<Dropdown menu={{ items }} trigger={["click"]}>
						<Button icon={<MoreOutlined />} />
					</Dropdown>
				) : null;
			},
		},
	];

	// Format file size
	const formatFileSize = (bytes) => {
		if (bytes === 0) return "0 Bytes";
		const k = 1024;
		const sizes = ["Bytes", "KB", "MB", "GB"];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
	};

	// Prepare data for table
	const tableData = [
		...folders.map((folder) => ({
			...folder,
			type: "folder",
			key: folder._id,
		})),
		...files.map((file) => ({ ...file, type: "file", key: file._id })),
	];

	const filterOption = (input, option) => {
		const text = option.children.props.children
			.filter((c) => typeof c === "string")
			.join(" ")
			.toLowerCase();

		return text.includes(input.toLowerCase());
	};

	return (
		<Layout title="57 Files" description="Files" style={{ padding: 24 }}>
			<Title level={2}>
				<FolderOpenOutlined /> File Manager
			</Title>

			{/* Breadcrumb */}
			<Breadcrumb style={{ marginBottom: 16 }}>
				<Breadcrumb.Item>
					<Button
						type="link"
						icon={<HomeOutlined />}
						onClick={loadRootFolders}
						style={{ padding: 0 }}
					>
						Home
					</Button>
				</Breadcrumb.Item>
				{breadcrumb.map((item) => (
					<Breadcrumb.Item key={item._id}>
						<Button
							type="link"
							onClick={() => loadFolder(item._id)}
							style={{ padding: 0 }}
						>
							{item.name}
						</Button>
					</Breadcrumb.Item>
				))}
			</Breadcrumb>

			{/* Actions */}
			<Space style={{ marginBottom: 16 }}>
				<Button
					type="primary"
					icon={<PlusOutlined />}
					onClick={() => setCreateFolderModal(true)}
				>
					New Folder
				</Button>
				<Button
					icon={<UploadOutlined />}
					onClick={() => setUploadFileModal(true)}
					disabled={!currentFolder}
				>
					Upload File
				</Button>
			</Space>

			{/* Table */}
			<Table
				columns={columns}
				dataSource={tableData}
				loading={loading}
				pagination={false}
			/>

			{/* Create Folder Modal */}
			<Modal
				title="Create New Folder"
				open={createFolderModal}
				onOk={handleCreateFolder}
				onCancel={() => {
					setCreateFolderModal(false);
					setNewFolderName("");
				}}
			>
				<Input
					placeholder="Folder name"
					value={newFolderName}
					onChange={(e) => setNewFolderName(e.target.value)}
					onPressEnter={handleCreateFolder}
				/>
			</Modal>

			{/* Upload File Modal */}
			<Modal
				title="Upload File"
				open={uploadFileModal}
				onOk={handleUploadFile}
				onCancel={() => {
					setUploadFileModal(false);
					setFileList([]);
				}}
			>
				<Upload
					beforeUpload={(file) => {
						setFileList([file]);
						return false;
					}}
					fileList={fileList}
					onRemove={() => setFileList([])}
					maxCount={1}
				>
					<Button icon={<UploadOutlined />}>Select File</Button>
				</Upload>
			</Modal>

			{/* Rename Modal */}
			<Modal
				title="Rename"
				open={renameModal}
				onOk={handleRename}
				onCancel={() => {
					setRenameModal(false);
					setRenameItem(null);
					setNewName("");
				}}
			>
				<Input
					placeholder="New name"
					value={newName}
					onChange={(e) => setNewName(e.target.value)}
					onPressEnter={handleRename}
				/>
			</Modal>

			{/* Share Modal */}
			<Modal
				title={`Share ${shareItem?.name}`}
				open={shareModal}
				onOk={handleShare}
				onCancel={() => {
					setShareModal(false);
					setShareItem(null);
					setSelectedUsers([]);
					setSharePermission("view");
				}}
				width={600}
			>
				<Space direction="vertical" style={{ width: "100%" }} size="large">
					<div>
						<Text strong>Select Users:</Text>
						<Select
							mode="multiple"
							style={{ width: "100%", marginTop: 8 }}
							placeholder="Select users to share with"
							value={selectedUsers}
							onChange={setSelectedUsers}
							optionFilterProp="children"
							filterOption={filterOption}
						>
							{users.map((user) => (
								<Option key={user._id} value={user._id}>
									<Space>
										<UserOutlined />
										{user.name} - {user.role} ({user.email})
									</Space>
								</Option>
							))}
						</Select>
					</div>
					<div>
						<Text strong>Permission Level:</Text>
						<Select
							style={{ width: "100%", marginTop: 8 }}
							value={sharePermission}
							onChange={setSharePermission}
						>
							<Option value="view">
								<Space>
									<UnlockOutlined />
									View Only - Can only view files
								</Space>
							</Option>
							<Option value="edit">
								<Space>
									<EditOutlined />
									Edit - Can view and upload
								</Space>
							</Option>
							{shareItem?.type === "folder" && (
								<Option value="admin">
									<Space>
										<LockOutlined />
										Admin - Full control including sharing
									</Space>
								</Option>
							)}
						</Select>
					</div>
				</Space>
			</Modal>

			{/* View Shared Users Modal */}
			<Modal
				title={`Access Control - ${shareItem?.name}`}
				open={viewSharedModal}
				onCancel={() => {
					setViewSharedModal(false);
					setSharedUsers([]);
					setFolderOwner(null);
					setShareItem(null);
				}}
				footer={[
					<Button key="close" onClick={() => setViewSharedModal(false)}>
						Close
					</Button>,
				]}
				width={600}
			>
				<Space direction="vertical" style={{ width: "100%" }} size="middle">
					<div>
						<Text strong>Owner:</Text>
						<List.Item style={{ paddingLeft: 0 }}>
							<List.Item.Meta
								avatar={<Avatar icon={<UserOutlined />} />}
								title={folderOwner?.name}
								description={folderOwner?.email}
							/>
							<Tag color="gold">OWNER</Tag>
						</List.Item>
					</div>

					<Divider />

					<div>
						<Text strong>Shared With ({sharedUsers.length}):</Text>
						{sharedUsers.length === 0 ? (
							<Text type="secondary" style={{ display: "block", marginTop: 8 }}>
								Not shared with anyone yet
							</Text>
						) : (
							<List
								dataSource={sharedUsers}
								renderItem={(item) => (
									<List.Item
										actions={[
											<Button
												danger
												size="small"
												icon={<DeleteOutlined />}
												onClick={() => handleRemoveAccess(item.user._id)}
											>
												Remove
											</Button>,
										]}
									>
										<List.Item.Meta
											avatar={<Avatar icon={<UserOutlined />} />}
											title={item.user.name}
											description={item.user.email}
										/>
										<Tag color={getPermissionColor(item.permission)}>
											{item.permission.toUpperCase()}
										</Tag>
									</List.Item>
								)}
							/>
						)}
					</div>
				</Space>
			</Modal>
		</Layout>
	);
};

export default Manager57FilesRoot;
